// Register — v2.8.22 design-system migration.
//
// Worker self-signup form, paired visually with Login (v2.8.0 redesign).
// Same `login-*` namespace from Login.css drives the page chrome (brand
// mark, stage, content slot, banners, foot link), so the two pages
// look like one cohesive auth surface. Register-specific bits use
// `register-*` for clarity.
//
// Replaces the legacy Auth.css `.auth-container` / `.auth-card` /
// `.auth-header` / `.auth-form` / `.password-toggle` /
// `.password-requirements` chain plus the `🙈 / 👁️ / ⚠️` emoji and
// inline `style={{ backgroundColor: settings.brand_color }}` on the
// submit button.

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useSettings } from '../../App';
import { authAPI } from '../../services/api';
import { Button, Field, Input, Card, Icon } from '../../ui';
import './Login.css';

function Register() {
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const brandName = settings?.brand_name || 'Full Circle Property';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await authAPI.register(formData);
      login(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-stage">
        <header className="login-mark">
          {settings?.brand_logo ? (
            <img src={settings.brand_logo} alt={brandName} className="login-mark__logo" />
          ) : (
            <span className="login-mark__short">{settings?.brand_short || 'FCP'}</span>
          )}
          <span className="login-mark__divider" aria-hidden="true" />
          <span className="login-mark__name">FieldWorks</span>
        </header>

        <div className="login-content">
          <h1 className="login-title">Create your account</h1>
          <p className="login-subtitle">Sign up as a worker.</p>

          {error && (
            <Card padding="sm" className="login-banner login-banner--error" role="alert">
              {error}
            </Card>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <Field label="Full name" required htmlFor="register-name">
              <Input
                id="register-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jane Smith"
                autoComplete="name"
                autoFocus
                required
                size="lg"
              />
            </Field>

            <Field label="Email address" required htmlFor="register-email">
              <Input
                id="register-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                autoComplete="email"
                required
                size="lg"
              />
            </Field>

            <Field
              label="Password"
              required
              htmlFor="register-password"
              hint="Must be at least 8 characters."
            >
              <div className="login-password">
                <Input
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  size="lg"
                />
                <button
                  type="button"
                  className="login-password__toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
                </button>
              </div>
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              className="login-submit"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </Button>

            <p className="login-foot">
              Already have an account? <Link to="/login" className="login-link">Sign in</Link>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;
